const momentTz = require('moment-timezone');
let _parent;

const businesshours = {
  init: p => (_parent = p),

  /**
   * Fetch business hours for a move and calculate move times
   * @param {String} moveId the id of the move to fetch business hours for
   * @param {Boolean} isMovePair whether the move is part of a pair of moves
   * @returns {Object} Object containing:
   *   - success: {Boolean} whether the operation was successful
   *   - If isMovePair is true:
   *     - moveOneClosure: {Object} first move details containing:
   *       - pickup_closure: {Object|null} closure info for pickup location if closed
   *       - delivery_closure: {Object|null} closure info for delivery location if closed
   *       - earliest_available_time: {Date} earliest time the move can start
   *     - moveTwoClosure: {Object} second move details with same structure as moveOneClosure
   *   - If isMovePair is false:
   *     - pickup_closure: {Object|null} closure info for pickup location if closed
   *     - delivery_closure: {Object|null} closure info for delivery location if closed
   *     - earliest_available_time: {Date} earliest time the move can start
   *   - If error occurs:
   *     - error: {Error} the error object
   *     - error_message: {String} error description
   */
  fetchLocationBusinessHoursAndCalcMoveTimes: async (moveId, isMovePair = false) => {
    const move = await _parent.businesshours.getMoveWithLane(moveId);
    if (!move.lane) {
      throw new Error('Move does not have a lane');
    }
    const pickupLocationId = move.lane.origin_location_id;
    const deliveryLocationId = move.lane.destination_location_id;
    const pickupBusinessHours = await _parent.businesshours.getBusinessHoursByLocation(pickupLocationId);
    const deliveryBusinessHours = await _parent.businesshours.getBusinessHoursByLocation(deliveryLocationId);
    if (isMovePair) {
      return _parent.businesshours.calcMovePairTimes(
        pickupBusinessHours,
        deliveryBusinessHours,
        move.ready_by,
        move.lane.duration_sec
      );
    }

    return _parent.businesshours.calcMoveTimesByLocations(
      pickupBusinessHours,
      deliveryBusinessHours,
      move.ready_by,
      move.lane.duration_sec
    );
  },

  /**
   * Calculate move times for a pair of moves
   * @param {Array} pickupBusinessHours array of business hours for pickup location
   * @param {Array} deliveryBusinessHours array of business hours for delivery location
   * @param {Date} readyBy the ready by time for the first move
   * @param {Number} estimatedDuration estimated duration in seconds for the moves (paired move will have inverse lane with matching duration)
   * @returns {Object} Object containing:
   *   - success: {Boolean} whether the operation was successful
   *   - moveOneClosure: {Object} first move details containing:
   *     - pickup_closure: {Object|null} closure info for pickup location if closed
   *     - delivery_closure: {Object|null} closure info for delivery location if closed
   *     - earliest_available_time: {Date} earliest time the first move can start
   *   - moveTwoClosure: {Object} second move details containing:
   *     - pickup_closure: {Object|null} closure info for pickup location if closed
   *     - delivery_closure: {Object|null} closure info for delivery location if closed
   *     - earliest_available_time: {Date} earliest time the second move can start
   */
  calcMovePairTimes: (pickupBusinessHours, deliveryBusinessHours, readyBy, estimatedDuration) => {
    try {
      //This should never happen because we have default records, but is here for the edge case that they don't exist
      if (!pickupBusinessHours && !deliveryBusinessHours) {
        throw new Error('No business hours found for pickup or delivery location');
      }
      //Used for delivery location of first move and pickup location of second move
      const readyByPlusDuration = momentTz(readyBy).add(estimatedDuration, 'seconds').toDate();
      //Used for delivery location of second move
      const readyByPlusDoubleDuration = momentTz(readyBy)
        .add(estimatedDuration * 2, 'seconds')
        .toDate();
      //Check if either location is closed, we need to return the closure reason if it is
      const pickupOneClosure = _parent.businesshours.locationIsClosed(pickupBusinessHours, readyBy);
      const deliveryOneClosure = _parent.businesshours.locationIsClosed(deliveryBusinessHours, readyByPlusDuration);
      const deliveryTwoClosure = _parent.businesshours.locationIsClosed(pickupBusinessHours, readyByPlusDoubleDuration);

      //If the location is closed at ready_by time, but will open after the ready_by time, set the earliestAvailableReadyBy to the open_time
      if (
        pickupOneClosure &&
        pickupOneClosure.open_time &&
        momentTz(readyBy).format('HH:mm:ss') <= pickupOneClosure.open_time
      ) {
        const readyByDate = momentTz(readyBy).format('YYYY-MM-DD');
        let earliestAvailableReadyByOne = momentTz(`${readyByDate} ${pickupOneClosure.open_time}`).toDate();
        let earliestAvailableReadyByTwo = momentTz(`${readyByDate} ${pickupOneClosure.open_time}`)
          .add(estimatedDuration, 'seconds')
          .toDate();

        return {
          success: true,
          moveOneClosure: {
            pickup_closure: pickupOneClosure,
            delivery_closure: deliveryOneClosure,
            earliest_available_time: earliestAvailableReadyByOne,
          },
          moveTwoClosure: {
            pickup_closure: deliveryOneClosure,
            delivery_closure: deliveryTwoClosure,
            earliest_available_time: earliestAvailableReadyByTwo,
          },
        };
      }
      if (
        !pickupOneClosure &&
        deliveryOneClosure &&
        deliveryOneClosure.open_time &&
        momentTz(readyByPlusDuration).format('HH:mm:ss') <= deliveryOneClosure.open_time
      ) {
        const readyByDate = momentTz(readyBy).format('YYYY-MM-DD');
        let earliestAvailableReadyByTwo = momentTz(`${readyByDate} ${deliveryOneClosure.open_time}`).toDate();
        let earliestAvailableReadyByOne = momentTz(`${readyByDate} ${deliveryOneClosure.open_time}`)
          .subtract(estimatedDuration, 'seconds')
          .toDate();

        return {
          success: true,
          moveOneClosure: {
            pickup_closure: pickupOneClosure,
            delivery_closure: deliveryOneClosure,
            earliest_available_time: earliestAvailableReadyByOne,
          },
          moveTwoClosure: {
            pickup_closure: deliveryOneClosure,
            delivery_closure: deliveryTwoClosure,
            earliest_available_time: earliestAvailableReadyByTwo,
          },
        };
      }

      //Find the earliest ready by time for the pair of moves The response will always be for the first move
      const earliestAvailableReadyByForPair = _parent.businesshours.findEarliestOpenTime(
        pickupBusinessHours,
        deliveryBusinessHours,
        readyBy,
        estimatedDuration,
        1,
        true
      );

      const earliestAvailableReadyByOne = earliestAvailableReadyByForPair;
      const earliestAvailableReadyByTwo = momentTz(earliestAvailableReadyByOne)
        .add(estimatedDuration, 'seconds')
        .toDate();

      return {
        success: true,
        moveOneClosure: {
          pickup_closure: pickupOneClosure,
          delivery_closure: deliveryOneClosure,
          earliest_available_time: earliestAvailableReadyByOne,
        },
        moveTwoClosure: {
          pickup_closure: deliveryOneClosure,
          delivery_closure: deliveryTwoClosure,
          earliest_available_time: earliestAvailableReadyByTwo,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error,
        error_message: error.message,
      };
    }
  },

  /**
   * Calculate move times for a single move
   * @param {Array} pickupBusinessHours array of business hours for pickup location
   * @param {Array} deliveryBusinessHours array of business hours for delivery location
   * @param {Date} readyBy the ready by time for the move
   * @param {Number} estimatedDuration estimated duration in seconds for the move
   * @returns {Object} Object containing:
   *   - success: {Boolean} whether the operation was successful
   *   - pickup_closure: {Object|null} closure info for pickup location if closed
   *   - delivery_closure: {Object|null} closure info for delivery location if closed
   *   - earliest_available_time: {Date} earliest time the move can start
   */
  calcMoveTimesByLocations: (pickupBusinessHours, deliveryBusinessHours, readyBy, estimatedDuration) => {
    try {
      //This should never happen because we have default records, but is here for the edge case that they don't exist
      if (!pickupBusinessHours && !deliveryBusinessHours) {
        throw new Error('No business hours found for pickup or delivery location');
      }

      const estimatedDeliveryBy = momentTz(readyBy).add(estimatedDuration, 'seconds').toDate();

      //Default earliest_ready_time as the ready_by time
      let earliestAvailableReadyBy = readyBy;
      //Check if either location is closed, we need to return the closure reason if it is
      const pickupClosure = _parent.businesshours.locationIsClosed(pickupBusinessHours, readyBy);
      const deliveryClosure = _parent.businesshours.locationIsClosed(deliveryBusinessHours, estimatedDeliveryBy);
      //If the location is closed at ready_by time, but will open after the ready_by time, set the earliestAvailableReadyBy to the open_time
      if (pickupClosure && pickupClosure.open_time && momentTz(readyBy).format('HH:mm:ss') <= pickupClosure.open_time) {
        const readyByDate = momentTz(readyBy).format('YYYY-MM-DD');
        earliestAvailableReadyBy = momentTz(`${readyByDate} ${pickupClosure.open_time}`).toDate();
        return {
          success: true,
          pickup_closure: pickupClosure,
          delivery_closure: deliveryClosure,
          earliest_available_time: earliestAvailableReadyBy,
        };
      }
      if (
        !pickupClosure &&
        deliveryClosure &&
        deliveryClosure.open_time &&
        momentTz(estimatedDeliveryBy).format('HH:mm:ss') <= deliveryClosure.open_time
      ) {
        const readyByDate = momentTz(readyBy).format('YYYY-MM-DD');
        earliestAvailableReadyBy = momentTz(`${readyByDate} ${deliveryClosure.open_time}`).toDate();
        return {
          success: true,
          pickup_closure: pickupClosure,
          delivery_closure: deliveryClosure,
          earliest_available_time: earliestAvailableReadyBy,
        };
      }

      //Find the earliest_ready_time if either location is closed on selected date
      if (pickupClosure || deliveryClosure) {
        earliestAvailableReadyBy = _parent.businesshours.findEarliestOpenTime(
          pickupBusinessHours,
          deliveryBusinessHours,
          readyBy,
          estimatedDuration,
          1
        );
      }

      return {
        success: true,
        pickup_closure: pickupClosure,
        delivery_closure: deliveryClosure,
        earliest_available_time: earliestAvailableReadyBy,
      };
    } catch (error) {
      return {
        success: false,
        error: error,
        error_message: error.message,
      };
    }
  },

  /**
   * Fetch business hours for a location
   * @param {String} locationId the id of the location to fetch business hours for
   * @returns {Array} array of business hours for the location
   */
  getBusinessHoursByLocation: async locationId => {
    try {
      const businessHours = await _parent.gql.query(GET_BUSINESS_HOURS, { locationId });
      if (!businessHours || !businessHours.data) {
        throw new Error('No business hours found for location');
      }
      return businessHours.data;
    } catch (error) {
      console.error(`Error fetching business hours for location ${locationId}: ${error}`);
      throw new Error(`Error fetching business hours for location ${locationId}: ${error}`);
    }
  },

  /**
   * Fetch move pickup and delivery locations
   * @param {String} moveId the id of the move to fetch pickup and delivery locations for
   * @returns {Object} Object containing:
   *   - id: {String} the id of the move
   *   - ready_by: {Date} the ready by time for the move
   *   - lane: {Object} the lane object containing:
   *     - id: {String} the id of the lane
   *     - duration_sec: {Number} the duration in seconds for the move
   *     - origin_location_id: {String} the id of the pickup location
   *     - destination_location_id: {String} the id of the delivery location
   */
  getMoveWithLane: async moveId => {
    try {
      const move = await _parent.gql.query(GET_MOVE_WITH_LANE, { moveId });
      return move.data[0];
    } catch (error) {
      console.error(`Error fetching move with lane for move ${moveId}: ${error}`);
      throw new Error(`Error fetching move with lane for move ${moveId}: ${error}`);
    }
  },

  /**
   * Check if the location is closed on a given date and return the closure reason if it is
   * @param {Array} businessHours array of objects with business hours for location
   * @param {Date} readyByDate date to check if the location is closed
   * @returns {Object | null} returns the business hour object if the location is closed, or null if it is not
   */
  locationIsClosed: (businessHours, readyByDate) => {
    const date = new Date(readyByDate);

    const readyDayOfMonth = momentTz(date).date();
    const readyMonth = momentTz(date).month() + 1;
    const readyYear = momentTz(date).year();
    const readyDayOfWeek = momentTz(date).day();
    const readyWeekOfMonth = momentTz(date).week() - momentTz(date).startOf('month').week() + 1;
    const readyByTime = momentTz(date).format('HH:mm:ss');

    //Find if there is an "one-off" closure for the given date
    const oneOff = businessHours.find(
      bh => bh.type === 'one-off' && bh.day === readyDayOfMonth && bh.month === readyMonth && bh.year === readyYear
    );
    if (oneOff && (!oneOff.is_open || oneOff.open_time >= readyByTime || oneOff.closed_time <= readyByTime)) {
      return oneOff;
    }

    //Find if there is a "holiday" closure for the given date
    const holiday = businessHours.find(
      bh =>
        (bh.type === 'customer-holiday' || bh.type === 'hopdrive-holiday') &&
        ((bh.day === readyDayOfMonth && bh.month === readyMonth) ||
          (bh.month === readyMonth && bh.week_of_month === readyWeekOfMonth && bh.day_of_week === readyDayOfWeek))
    );
    if (holiday && (!holiday.is_open || holiday.open_time >= readyByTime || holiday.closed_time <= readyByTime)) {
      return holiday;
    }

    //Find if there is a "business-hours" closure for the given day of the week
    const locationBusinessHours = businessHours.filter(
      bh => bh.location_id !== null && bh.type === 'business-hours' && bh.day_of_week === readyDayOfWeek
    );
    for (const lbh of locationBusinessHours) {
      if (!lbh.is_open || lbh.open_time >= readyByTime || lbh.closed_time <= readyByTime) {
        return lbh;
      }
    }

    //If there is no location specific "business-hour" data for the given day of the week, check the default hours
    const defaultBusinessHours = businessHours.find(
      bh => bh.type === 'default-hours' && bh.day_of_week === readyDayOfWeek
    );
    if (
      defaultBusinessHours &&
      (!defaultBusinessHours.is_open ||
        defaultBusinessHours.open_time >= readyByTime ||
        defaultBusinessHours.closed_time <= readyByTime)
    ) {
      return defaultBusinessHours;
    }
    // If no closure matches, the location is open
    return null;
  },

  /**
   * Find the earliest open time for a location
   * @param {Array} pickupLocationHours array of business hours for the pickup location
   * @param {Array} deliveryLocationHours array of business hours for the delivery location
   * @param {Date} pickupTime the pickup time for the move
   * @param {Number} estMoveDuration estimated duration in seconds for the move
   * @param {Number} dayCount the number of days to add to the pickup time
   * @param {Boolean} isMovePair whether the move is a pair of moves
   * @returns {Date} the earliest open time for the location
   */
  findEarliestOpenTime: (
    pickupLocationHours,
    deliveryLocationHours,
    pickupTime,
    estMoveDuration,
    dayCount = 1,
    isMovePair = false
  ) => {
    if (
      !pickupLocationHours ||
      !deliveryLocationHours ||
      pickupLocationHours.length === 0 ||
      deliveryLocationHours.length === 0
    ) {
      throw new Error('No business hours found for pickup or delivery location');
    }
    //Safety check that the day count is not greater than 7
    if (dayCount > 7) {
      throw new Error('Failed to find an open day 7 times in a row');
    }
    const datePickupTime = momentTz(pickupTime).add(dayCount, 'day').toDate();
    const estimatedDeliveryBy = momentTz(datePickupTime).add(estMoveDuration, 'seconds').toDate();
    //If move is a round trip, then add the move duration to the estimated delivery by time
    const estimatedReturnDeliveryBy = momentTz(estimatedDeliveryBy)
      .add(estMoveDuration * 2, 'seconds')
      .toDate();

    //Find all matching business hours for the pickupTime + day count
    const matchingPickupHours = _parent.businesshours.findMatchingBusinessHours(pickupLocationHours, datePickupTime);
    const matchingDeliveryHours = _parent.businesshours.findMatchingBusinessHours(
      deliveryLocationHours,
      estimatedDeliveryBy
    );

    //If any matched business hours have is_open = false, advance to next day and recurse
    if (matchingPickupHours.find(bh => !bh.is_open) || matchingDeliveryHours.find(bh => !bh.is_open)) {
      return _parent.businesshours.findEarliestOpenTime(
        pickupLocationHours,
        deliveryLocationHours,
        pickupTime,
        estMoveDuration,
        dayCount + 1,
        isMovePair
      );
    }
    //If location is open on the date, then return earliest open time on a business hour (or default hour)
    const earliestPickupOpenTime = _parent.businesshours.findStartTime(matchingPickupHours, pickupTime);
    const earliestDeliveryOpenTime = _parent.businesshours.findStartTime(matchingDeliveryHours, estimatedDeliveryBy);
    if (isMovePair) {
      //Find the earliest open time for the return delivery (back to the pickup location)
      var earliestReturnDeliveryTime = _parent.businesshours.findStartTime(
        matchingPickupHours,
        estimatedReturnDeliveryBy
      );
    }
    const adjustedDate = momentTz(pickupTime).add(dayCount, 'days').format('YYYY-MM-DD');
    let adjustedEarliestPickupOpenTime = momentTz.utc(`${adjustedDate} ${earliestPickupOpenTime}`).toDate();

    console.log('earliestPickupOpenTime', earliestPickupOpenTime, 'adjustedEarliestPickupOpenTime', adjustedEarliestPickupOpenTime);
    //If delivery location is not open when the move is estimated to be delivered, then return the delivery location's earliest open time minus the move duration
    if (momentTz.utc(earliestPickupOpenTime).add(estMoveDuration, 'seconds').toDate() > earliestDeliveryOpenTime) {
      adjustedEarliestPickupOpenTime = momentTz.utc(`${adjustedDate} ${earliestDeliveryOpenTime}`)
        .subtract(estMoveDuration, 'seconds')
        .toDate();
    }
    //If delivery location is not open when the second move is estimated to be delivered, then return the earliest open time for the return delivery minus both moves' move duration
    if (
      isMovePair &&
      momentTz.utc(adjustedEarliestPickupOpenTime).add(estMoveDuration, 'seconds').toDate() > earliestReturnDeliveryTime
    ) {
      adjustedEarliestPickupOpenTime = momentTz.utc(`${adjustedDate} ${earliestDeliveryOpenTime}`)
        .subtract(estMoveDuration * 2, 'seconds')
        .toDate();
    }

    return adjustedEarliestPickupOpenTime;
  },

  /**
   * Find all business hours that match a given date, regardless of type or is_open status
   * @param {Array} businessHours array of objects with business hours for location
   * @param {Date} readyByDate date to check for matching business hours
   * @returns {Array} returns array of all matching business hour objects
   */
  findMatchingBusinessHours: (businessHours, readyByDate) => {
    const readyDayOfMonth = readyByDate.getDate();
    const readyMonth = readyByDate.getMonth() + 1;
    const readyYear = readyByDate.getFullYear();
    const readyDayOfWeek = readyByDate.getDay();
    const readyWeekOfMonth = Math.ceil((readyDayOfMonth - 1 - readyDayOfWeek) / 7);

    const matchingHours = businessHours.filter(bh => {
      // Match one-off dates
      if (bh.type === 'one-off') {
        return bh.day === readyDayOfMonth && bh.month === readyMonth && bh.year === readyYear;
      }

      // Match holidays and hopdrive-holidays
      if (bh.type === 'customer-holiday' || bh.type === 'hopdrive-holiday') {
        return (
          (bh.day === readyDayOfMonth && bh.month === readyMonth) ||
          (bh.month === readyMonth && bh.week_of_month === readyWeekOfMonth && bh.day_of_week === readyDayOfWeek)
        );
      }

      // Match business-hours
      if (bh.type === 'business-hours') {
        return bh.day_of_week === readyDayOfWeek;
      }

      // Match default-hours
      if (bh.type === 'default-hours') {
        return bh.day_of_week === readyDayOfWeek;
      }

      return false;
    });
    return matchingHours;
  },

  /**
   * Find the earliest open time for a location
   * @param {Array} matchingHours array of business hours for the location
   * @returns {String} the earliest open time for the location
   */
  findStartTime: matchingHours => {
    if (!matchingHours || matchingHours.length === 0) {
      return null;
    }

    //First check one-off, then holiday, then business-hours, then default-hours
    const oneOffHours = matchingHours.filter(bh => bh.type === 'one-off' && bh.is_open && bh.open_time);
    if (oneOffHours.length === 1) {
      return oneOffHours[0].open_time;
    } else if (oneOffHours.length > 1) {
      //If multiple hours are found, then return the earliest open_time
      let sortedHours = oneOffHours.sort((a, b) => {
        return momentTz(a.open_time).diff(momentTz(b.open_time));
      });
      return sortedHours[0].open_time;
    }
    const holidayHours = matchingHours.filter(
      bh => (bh.type === 'customer-holiday' || bh.type === 'hopdrive-holiday') && bh.is_open && bh.open_time
    );
    if (holidayHours.length === 1) {
      return holidayHours[0].open_time;
    } else if (holidayHours.length > 1) {
      //If multiple hours are found, then return the earliest open_time
      let sortedHours = holidayHours.sort((a, b) => {
        return momentTz(a.open_time).diff(momentTz(b.open_time));
      });
      return sortedHours[0].open_time;
    }
    const businessHours = matchingHours.filter(bh => bh.type === 'business-hours' && bh.is_open && bh.open_time);
    if (businessHours.length === 1) {
      return businessHours[0].open_time;
    } else if (businessHours.length > 1) {
      //If multiple hours are found, then return the earliest open_time
      let sortedHours = businessHours.sort((a, b) => {
        return momentTz(a.open_time).diff(momentTz(b.open_time));
      });
      return sortedHours[0].open_time;
    }
    const defaultHours = matchingHours.filter(bh => bh.type === 'default-hours' && bh.is_open && bh.open_time);
    if (defaultHours.length === 1) {
      return defaultHours[0].open_time;
    } else if (defaultHours.length > 1) {
      //If multiple hours are found, then return the earliest open_time
      let sortedHours = defaultHours.sort((a, b) => {
        return momentTz(a.open_time).diff(momentTz(b.open_time));
      });
      return sortedHours[0].open_time;
    }
    return null; // Return null if no matching hours found
  },

  /**
   * Handle the earliest available time for a pair of moves
   * @param {Array} moveIds array of move ids to get the earliest available times for
   * @returns {Object} response object containing:
   *   - earliestAvailableTimeMessage: {String} the message to display to the user
   *   - earliestAvailableTime: {Object} the earliest available time for the moves
   */
  handleEarliestAvailableTimeRoundTrip: async moveIds => {
    const locationHoursCheck = await _parent.businesshours.fetchLocationBusinessHoursAndCalcMoveTimes(moveIds[0], true);

    await Promise.all(
      moveIds.map(async (moveId, index) => {
        const timeToUse =
          index === 0
            ? locationHoursCheck.moveOneClosure.earliest_available_time
            : locationHoursCheck.moveTwoClosure.earliest_available_time;
        return await _parent.businesshours.updateEarliestAvailableTime(moveId, timeToUse);
      })
    );

    let earliestAvailableTimeMessage = 'Move successfully created';
    if (locationHoursCheck.moveOneClosure.pickup_closure || locationHoursCheck.moveOneClosure.delivery_closure) {
      earliestAvailableTimeMessage = await _parent.businesshours.generateClosureMessage(
        locationHoursCheck.moveOneClosure.pickup_closure,
        locationHoursCheck.moveOneClosure.delivery_closure,
        locationHoursCheck.moveOneClosure.earliest_available_time,
        true
      );
    }

    return {
      earliestAvailableTimeMessage,
      earliestAvailableTime: {
        moveOne: locationHoursCheck.moveOneClosure.earliest_available_time,
        moveTwo: locationHoursCheck.moveTwoClosure.earliest_available_time,
      },
    };
  },

  /**
   * Handle the earliest available time for a single move
   * @param {String} moveId the id of the move to get the earliest available time for
   * @returns {Object} response object containing:
   *   - earliestAvailableTimeMessage: {String} the message to display to the user
   *   - earliestAvailableTime: {Date} the earliest available time for the move
   */
  handleEarliestAvailableTime: async moveId => {
    const locationHoursCheck = await _parent.businesshours.fetchLocationBusinessHoursAndCalcMoveTimes(moveId, false);
    await _parent.businesshours.updateEarliestAvailableTime(moveId, locationHoursCheck.earliest_available_time);

    let earliestAvailableTimeMessage = 'Move successfully created';
    if (locationHoursCheck.pickup_closure || locationHoursCheck.delivery_closure) {
      earliestAvailableTimeMessage = await _parent.businesshours.generateClosureMessage(
        locationHoursCheck.pickup_closure,
        locationHoursCheck.delivery_closure,
        locationHoursCheck.earliest_available_time,
        false
      );
    }
    return { earliestAvailableTimeMessage, earliestAvailableTime: locationHoursCheck.earliest_available_time };
  },

  /**
   * Update the earliest available time for a move
   * @param {String} moveId the id of the move to update
   * @param {Date} earliestAvailableTime the earliest available time to set for the move
   * @returns {Object} the updated move object
   */
  updateEarliestAvailableTime: async (moveId, earliestAvailableTime) => {
    const move = await _parent.gql.mutation(UPDATE_EARLIEST_AVAILABLE_TIME, { moveId, earliestAvailableTime });
    return move.data[0];
  },

  /**
   * Generate a closure message for a move
   * @param {Object} pickupClosure the closure info for the pickup location
   * @param {Object} deliveryClosure the closure info for the delivery location
   * @param {Date} earliestReadyTime the earliest ready time for the move
   * @param {Boolean} secondMoveInPair whether the move is a pair of moves
   * @returns {String} the closure message for the move
   */
  generateClosureMessage: (pickupClosure, deliveryClosure, earliestReadyTime, secondMoveInPair = false) => {
    const closedLocation = pickupClosure || deliveryClosure || null;
    let earliestAvailableTimeMessage = '';
    if (closedLocation) {
      const locationClosed = pickupClosure ? 'pickup location' : 'delivery location';
      const timeOfMove = secondMoveInPair ? 'time of the second move' : 'selected time';
      let closureReason = '';
      if (closedLocation.type === 'customer-holiday' || closedLocation.type === 'hopdrive-holiday') {
        closureReason = ` due to a holiday (${closedLocation.name})`;
      } else if (closedLocation.type === 'one-off') {
        closureReason = ` due to a one-off closure (${closedLocation.name})`;
      } else if (closedLocation.type === 'business-hours' || closedLocation.type === 'default-hours') {
        const dayName = momentTz.weekdays()[closedLocation.day_of_week];
        closureReason = ` due to ${dayName} hours`;
      }
      const formattedDate = momentTz(earliestReadyTime).utc().format('ddd. h:mma MM/DD/YYYY');
      earliestAvailableTimeMessage = `The ${locationClosed} may be closed at the ${timeOfMove} ${closureReason}. Because of this, the move may not be available for pickup until: ${formattedDate}.`;
    }
    return earliestAvailableTimeMessage;
  },
};

module.exports = businesshours;

const GET_MOVE_WITH_LANE = `
  query getMoveWithLane($moveId: bigint!) {
    moves(where: {id: {_eq: $moveId}}) {
      id
      ready_by
      lane {
        id
        duration_sec
        origin_location_id
        destination_location_id
      }
    }
  }`;

const GET_BUSINESS_HOURS = `
  query getBusinessHours($locationId: bigint!) {
    businesshours(where: {
      type: {_neq: "federal-holiday"},
      _or: [
        {location_id: {_eq: $locationId}},
        {_and: {location_id: {_is_null: true}, type: { _eq: "default-hours" }}
        }
      ]}) {
      id
      closed_time
      created_at
      created_by
      day
      day_of_week
      is_open
      location_id
      month
      name
      open_time
      type
      updated_at
      updated_by
      week_of_month
      year
    }
  }
`;

const UPDATE_EARLIEST_AVAILABLE_TIME = `
  mutation updateEarliestAvailableTime($moveId: bigint!, $earliestAvailableTime: timestamptz!) {
    update_moves(where: {id: {_eq: $moveId}}, _set: { earliest_available_time: $earliestAvailableTime}) {
      affected_rows
    }
  }
`;
